import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const MetricCell = ({ value, highlight }) => (
  <td className={`px-4 py-3 text-sm text-right font-medium ${highlight ? 'text-violet-600' : value > 0 ? 'text-slate-700' : 'text-slate-300'}`}>
    {value ?? 0}
  </td>
);

const UserAnalytics = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/events`);
        setEvents(Array.isArray(res.data) ? res.data : []);
        setError('');
      } catch (err) {
        setEvents([]);
        setError(err.response?.data?.error || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto flex justify-center items-center min-h-[40vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading analytics…</p>
        </div>
      </div>
    );
  }

  // Totals row
  const totals = events.reduce((acc, e) => ({
    batch_count: acc.batch_count + (e.batch_count ?? 0),
    photo_count: acc.photo_count + (e.photo_count ?? 0),
    page_visits: acc.page_visits + (e.page_visits ?? 0),
    face_verified: acc.face_verified + (e.face_verified_count ?? e.verification_count ?? 0),
    download_image: acc.download_image + (e.download_image_count ?? 0),
    download_zip: acc.download_zip + (e.download_zip_count ?? 0),
    view_all_photo: acc.view_all_photo + (e.view_all_photo_count ?? 0),
  }), { batch_count: 0, photo_count: 0, page_visits: 0, face_verified: 0, download_image: 0, download_zip: 0, view_all_photo: 0 });

  const columns = [
    { key: 'batch_count',   label: 'Batches',        icon: '📦', color: 'blue'   },
    { key: 'photo_count',   label: 'Photos',          icon: '🖼',  color: 'emerald'},
    { key: 'page_visits',   label: 'Page visits',     icon: '👁',  color: 'slate'  },
    { key: 'face_verified', label: 'Face verified',   icon: '✅',  color: 'violet' },
    { key: 'download_image',label: 'Img downloads',   icon: '⬇',  color: 'slate'  },
    { key: 'download_zip',  label: 'ZIP downloads',   icon: '📥',  color: 'slate'  },
    { key: 'view_all_photo',label: 'All photos views',icon: '🗂',  color: 'slate'  },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-5">

      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Analytics</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          {events.length === 0 ? 'No events yet' : `${events.length} event${events.length !== 1 ? 's' : ''} · per-event engagement breakdown`}
        </p>
      </div>

      {error && (
        <div className="px-4 py-3 bg-rose-50 border border-rose-200 rounded-2xl text-sm text-rose-700">{error}</div>
      )}

      {/* Summary stat cards */}
      {events.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Page visits',   value: totals.page_visits,   color: 'bg-slate-100',   iconColor: 'text-slate-500' },
            { label: 'Face verified', value: totals.face_verified,  color: 'bg-violet-100',  iconColor: 'text-violet-600' },
            { label: 'Img downloads', value: totals.download_image, color: 'bg-blue-100',    iconColor: 'text-blue-600'   },
            { label: 'ZIP downloads', value: totals.download_zip,   color: 'bg-emerald-100', iconColor: 'text-emerald-600'},
          ].map(({ label, value, color, iconColor }) => (
            <div key={label} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                <svg className={`w-5 h-5 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <p className="text-xl font-bold text-slate-900 leading-none">{value}</p>
                <p className="text-xs font-medium text-slate-500 mt-0.5">{label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Per-event table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Per-Event Breakdown</h2>
        </div>

        {events.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #ede9fe, #e0e7ff)' }}>
              <svg className="w-7 h-7 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-slate-600">No events yet</p>
            <p className="text-xs text-slate-400 mt-1">Create an event to start tracking analytics.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider sticky left-0 bg-slate-50/50">Event</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Batches</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Photos</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Page visits</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-violet-500 uppercase tracking-wider">Face verified</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Img DL</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">ZIP DL</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">All photos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {events.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-semibold text-slate-800 max-w-[160px] truncate">{e.name}</td>
                    <MetricCell value={e.batch_count ?? 0} />
                    <MetricCell value={e.photo_count ?? 0} />
                    <MetricCell value={e.page_visits ?? 0} />
                    <MetricCell value={e.face_verified_count ?? e.verification_count ?? 0} highlight />
                    <MetricCell value={e.download_image_count ?? 0} />
                    <MetricCell value={e.download_zip_count ?? 0} />
                    <MetricCell value={e.view_all_photo_count ?? 0} />
                  </tr>
                ))}
              </tbody>
              {events.length > 1 && (
                <tfoot>
                  <tr className="bg-slate-50 border-t-2 border-slate-200">
                    <td className="px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Total</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-slate-700">{totals.batch_count}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-slate-700">{totals.photo_count}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-slate-700">{totals.page_visits}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-violet-600">{totals.face_verified}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-slate-700">{totals.download_image}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-slate-700">{totals.download_zip}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-slate-700">{totals.view_all_photo}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserAnalytics;
